import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Typography,
  Button,
} from "@material-tailwind/react";
import SideBar from './SideBar';
import PreviousSSD from '../../sanityInterfaces/PreviousSSD';

// Define interfaces for the CardList component
interface BioType {
  children: Array<{ text: string }>;
}

interface DataContent {
  _id: string;
  name: string;
  url: string;
  Biography: BioType[];
  image: {
    asset: {
      _ref: string;
    };
  };
}

interface CardListProps {
  data: DataContent[];
}

// CardList component
const CardList = ({ data }: CardListProps) => {
  const [selectedPerson, setSelectedPerson] = useState<DataContent | null>(null);

  return (
    <div className="flex justify-start gap-6 p-4 items-center flex-wrap">
      {data.length > 0 &&
        data.map((item) => {
          const imgUrl = item.image?.asset
            ? `https://cdn.sanity.io/images/79vgbaej/production/${item.image.asset._ref.slice(6, -4)}.${item.image.asset._ref.slice(-3)}`
            : "";

          return (
            <Card
              key={item._id}
              className="relative bg-white w-52 shadow-md rounded-xl"
            >
              <CardHeader floated={false} className="h-40">
                {imgUrl && <img className="object-cover h-full w-full" src={imgUrl} alt={item.name} />}
              </CardHeader>

              <CardBody className="text-center">
                {/* Name */}
                <Typography color="blue-gray" className="mb-2 text-md font-semibold">
                  {item.name}
                </Typography>

                {/* URL */}
                <Typography color="blue-gray" className="font-medium" textGradient>
                  <a href={item.url} className="text-blue-500 text-sm font-semibold">
                    {item.url}
                  </a>
                </Typography>

                {/* Biography Button */}
                <Button
                  variant="outlined"
                  size="sm"
                  color="red"
                  className="mt-4 rounded-full"
                  onClick={() => setSelectedPerson(item)}
                >
                  Biography
                </Button>
              </CardBody>
            </Card>
          );
        })}

      {/* Sidebar */}
      <SideBar
        isOpen={!!selectedPerson}
        onClose={() => setSelectedPerson(null)}
        name={selectedPerson?.name || ""}
        url={selectedPerson?.url || ""}
        image={
          selectedPerson?.image?.asset
            ? `https://cdn.sanity.io/images/79vgbaej/production/${selectedPerson.image.asset._ref.slice(6, -4)}.${selectedPerson.image.asset._ref.slice(-3)}`
            : ""
        }
        biography={selectedPerson?.Biography}
      />
    </div>
  );
};

// SectionHeader component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="flex items-center mt-6 mb-4">
    <img
      className="w-8 mx-4"
      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAACXBIWXMAAAsTAAALEwEAmpwYAAADj0lEQVR4nO2aOYgUURCGn4omu9P1V4/rGokooqiIieK1qIGgiQceiahgIopnYmqkrqCBCCaG3gpGsjqvelgEQxMPRNYrUkFF8MLVwJXq7nFnm+mZ7rWHmcYteMH0665XX9fxat6MMWPyn8gNYyZZ4JQQ3grhjQC9es3kTQToFfDQyIFekzcR9UQExBLembyJhMaXmZeVmJdXPrdXzCMaMsOjcv/fa8wrPKAndr7WoCbmVO2YTwCScl5GAPHJ7EEaeKI5IHibPUiShVPkQNb6TNYLa0500lWdM+0LgmyHGQOJkTGPYCy0htoiRyxwU5iX3i8WC/2dnZMF2GXBH3OW7LheS8cDYyZa4FpuQKzrLonTU3acmbkBues4bpwe9UpuQIRoRpyeIF/yAsK8Nk6PBVbmBsQDeuL0eI67JjcgtlCYFavHKS7KDwi4P1YP8b3cgAh4qES0MKrDAxak1WNaDSLgclSHJfTlEeR5DZAn+QMhvl0D5E5uQCzhhxCfv03EUR1h83hBwIPtC0L4JsTHSx0dUxrp6u/qmqrHSz50Ar0WeCbEl63D2/75rKvBYm/uFotz0uosMc8X8Ps0oWbBL0pEm5oE4m4erV4L7BgZmvxQgIOe684tdXd36Cgzz7PAISF+NHwveo8ZMz5TkP6urs4MTjAHPYf31DNO54R573COjeJ0fxTlN80Y1GbSJLWFaFUFxiPa2DYg6olG698wZoIQnysXirMDe7AvfP65fsdpOYjmRJJYF3KPhM8MKFQAhseBV9wtLQcR4ECjte8QTbeEr0HVwvrKdUt0OHwZl1oOotWp3rpDxoyzgNQyWKtZCPes5SB6XFS9jo0kvZC7OzCWP2hnUD2nzwaA+NJykOrS7REfDd/8WfVE0AHwpzAEt0dt6nNdx58jfE4O4v8c1tzQEmCnBf8KDdee7Fb4xvvqdAYpQ4v4ZDNAdMeuXsdjXuf3bZV5whdhnla3khFfTAyizZoPk7VniB9Fy6/nuIs1J+pVNS2/le83qcpv1uK/GPAr31jmvdH5cqE4WwhX4vYYC+wPX8ZAqg2xGaLtRWjMoLYdJvlzqwX804J/V+8rLRULnPkLA+zTkIm71w+nwBM/w+p2wrSLaJkV4PRwzuCx7ti62Wlp1uFXJ3KPVHLC9wTxiVG18c0WATYI+HWCAjHQNuEUJ/7fRMjdawlXS1uEU5z4fxMRd6slXKsRfikU3vo9FuGpllitTnGJ/QdCSqBcTR3fgwAAAABJRU5ErkJggg=="
      alt=""
      aria-hidden
    />
    <h1 className="text-left text-xl text-red-500 font-bold">{title}</h1>
  </div>
);

interface ContentProps {
  data: PreviousSSD;
}

const ChairsContent: React.FC<ContentProps> = (props) => {
  // Helper function to transform data for CardList
  const transformData = (people: any[]): DataContent[] => {
    return people.map((person, index) => ({
      _id: person._id || `person-${index}`,
      name: person.name || '',
      url: person.url || '',
      Biography: person.Biography || [],
      image: person.image || { asset: { _ref: '' } },
    }));
  };

  return (
    <div className="grid justify-items-center">
      {props?.data ? (
        <div className="grid gap-4 bg-gray-100/40 rounded-md p-4 w-full">
          <SectionHeader title="SSD General Chairs" />
          <CardList data={transformData(props?.data?.SSD_General_Chairs || [])} />

          <SectionHeader title="SSD Co-General Chairs" />
          <CardList data={transformData(props?.data?.SSDCOGENERALCHAIRS || [])} />

          <SectionHeader title="Honorary Chairs" />
          <CardList data={transformData(props?.data?.SSDHONORARYCHAIRS || [])} />

          <SectionHeader title="Organizing Committee Chairs" />
          <CardList data={transformData(props?.data?.SSDORGANIZINGCOMMITTEECHAIRS || [])} />

          <SectionHeader title="SAC Chairs" />
          <CardList data={transformData(props?.data?.SACCHAIRS || [])} />

          <SectionHeader title="SCI Chairs" />
          <CardList data={transformData(props?.data?.SCICHAIRS || [])} />

          <SectionHeader title="CSP Chairs" />
          <CardList data={transformData(props?.data?.CSPCHAIRS || [])} />

          <SectionHeader title="Plenary Sessions Chairs" />
          <CardList data={transformData(props?.data?.PLENARYSESSIONSCHAIRS || [])} />

          <SectionHeader title="Publication Chairs" />
          <CardList data={transformData(props?.data?.PUBLICATIONCHAIRS || [])} />
        </div>
      ) : null}
    </div>
  );
};

export default ChairsContent;